'use client'
import React from 'react'
import Image from 'next/image'
import { useLocale, useTranslations } from 'next-intl'
import { ArrowLeft, CalendarDays, Clock, Tag } from 'lucide-react'
import { Link } from '@/app/i18n/navigation'
import { useBlog, useBlogs } from '@/app/services/blog'
import { formatBlogDate, getCoverUrl, getReadingMinutes, prepareBlogHtml } from '@/app/utils/blog'
import type { Blog } from '@/app/types/blog'
import ArticleSkeleton from './ArticleSkeleton'
import ArticleError from '../../_components/ArticleError'
import { BlogCard, CategoryChips } from '../../BlogCard'

export function BlogPage({ slug }: { slug: string }) {
  const t = useTranslations('Blog')
  const language = useLocale()
  const { data, isFetching, isError } = useBlog(slug)
  const { data: latest } = useBlogs({ page: 1, page_size: 4, language, sort: 'desc' })

  if (isFetching) return <ArticleSkeleton />

  if (isError) return <ArticleError errorMessage={t('article_error_text')} displayButton />

  // API answered with an empty data array -> no article with this slug
  if (!data?.slug) return <ArticleError errorMessage={t('article_not_found')} displayButton />

  const cover = getCoverUrl(data, 'cover')
  const date = formatBlogDate(data.created_at, language)
  const related = (latest as Blog[]).filter(b => b.slug !== data.slug).slice(0, 3)

  return (
    <>
      <article>
        {/* 1. Header & metadata */}
        <header className="px-6 pt-10 md:pt-14 pb-10">
          <div className="max-w-5xl mx-auto">
            <Link href="/blog" className="inline-flex items-center gap-2 text-[#008D63] font-bold mb-8 hover:gap-3 transition-all">
              <ArrowLeft size={18} />
              {t('back_to_blog')}
            </Link>
            <CategoryChips blog={data} max={3} />
            <h1 className="text-[#064E3B] text-3xl md:text-5xl font-bold leading-tight mt-5 mb-5">{data.title}</h1>
            {data.description && (
              <p className="text-[#064E3B]/75 text-lg md:text-xl leading-relaxed mb-8">{data.description}</p>
            )}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-[#064E3B]/70 text-sm">
              {data.author && (
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-primary text-white font-bold flex items-center justify-center">
                    {data.author.trim().charAt(0).toUpperCase()}
                  </span>
                  <span className="font-bold text-[#064E3B] text-base">{data.author}</span>
                </div>
              )}
              {date && (
                <span className="flex items-center gap-1.5">
                  <CalendarDays size={16} />
                  <time dateTime={data.created_at}>{date}</time>
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <Clock size={16} />
                {getReadingMinutes(data.content)} {t('min_read')}
              </span>
            </div>
          </div>
        </header>

        {/* 2. Featured image */}
        {cover && (
          <div className="px-4 md:px-10">
            <div className="relative max-w-5xl mx-auto aspect-[16/9] rounded-[32px] md:rounded-[40px] overflow-hidden shadow-sm bg-[#064E3B]/10">
              <Image
                src={cover}
                alt={data.alt || data.title}
                fill
                priority
                unoptimized
                sizes="(min-width: 1024px) 1024px, 100vw"
                className="object-cover"
              />
            </div>
          </div>
        )}

        {/* 3. Main content */}
        <div className="px-6 py-12 md:py-16">
          <div className="max-w-5xl mx-auto bg-white rounded-[32px] px-6 py-8 md:px-12 md:py-12">
            <div className="blog-content" dangerouslySetInnerHTML={{ __html: prepareBlogHtml(data.content) }} />

            {/* 4. Tags */}
            {data.tags && data.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t-2 border-dashed border-[#A7D3AF]">
                <p className="text-[#064E3B] font-bold mb-4">{t('tags')}</p>
                <ul className="flex flex-wrap gap-2">
                  {data.tags.map((tag: string, i: number) => (
                    <li
                      key={i}
                      className="flex items-center gap-1.5 border-2 border-dashed border-[#059669] text-[#059669] bg-primary-light text-sm font-semibold px-3 py-1 rounded-lg">
                      <Tag size={14} />
                      {tag}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </article>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="px-4 md:px-10 pb-20">
          <div className="flex items-end justify-between gap-4 mb-6 px-2">
            <h2 className="text-[#064E3B] text-2xl md:text-3xl font-bold">
              {t('more_articles')}{' '}
              <span className="bg-[#FFE37B] px-2 py-0.5 rounded-md rotate-[-2deg] inline-block">{t('more_highlight')}</span>
            </h2>
            <Link href="/blog" className="hidden sm:inline-flex text-[#008D63] font-bold hover:underline">
              {t('view_all')}
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map(b => (
              <BlogCard key={b.id} blog={b} locale={language} labels={{ minRead: t('min_read'), readMore: t('read_more') }} />
            ))}
          </div>
        </section>
      )}
    </>
  )
}
